import React, { FunctionComponent, ReactElement } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';
import { useMedia } from 'react-media-match';
import useConfig from '@empiriecom/module-components/hooks/useConfig';
import Card, { CardHeadline } from '@/src/components/Card';
import { RatingIcons } from '@/src/components/icons';
import { Rating } from '@/interfaces/Rating';
import PercentageCircle from './RecommendCircle';
import Tags from './RatingTags';
import CreateRating from '../CreateRating';
import CreateRatingContextProvider from '../CreateRating/CreateRatingContext';

const RatingsSummaryWrapper = styled.div<{ isMobile: boolean; hasSummaryValues: boolean }>`
  width: ${({ isMobile }) => (isMobile ? '100%;' : '40%;')};
  ${({ hasSummaryValues }) => !hasSummaryValues && 'width: 100%;'}
`;

export const StarsWrapper = styled.div`
  display: flex;
  justify-content: flex-start;
  align-items: center;
  width: 9.25rem;
  padding: 1rem;
`;

export const RatingStars = styled(RatingIcons)`
  width: 100%;
  margin-right: 0.25rem;
`;

export const RecommendCountWrapper = styled.div`
  display: flex;
  justify-content: flex-start;
  align-items: center;
  padding: 0 1rem;
  margin-bottom: 1rem;
`;

const TagsWrapper = styled.div`
  padding: 0 1rem;
  .headline {
    padding-left: 0;
  }
`;

interface RatingsSummaryProps {
  /** The rating. */
  response: Rating;
}

export const RatingsSummary: FunctionComponent<RatingsSummaryProps> = ({
  response,
}): ReactElement => {
  const isMobile = useMedia({ desktop: false, mobile: true, tablet: false });
  const {
    customerRatings: { iconType },
  } = useConfig();
  return (
    <RatingsSummaryWrapper
      isMobile={isMobile}
      data-testid="ratings-summary-wrapper"
      hasSummaryValues={response.summaryValues !== null}
    >
      {response.summaryValues !== null && (
        <Card
          headline={
            <CardHeadline>
              <FormattedMessage
                id="ratings.summary.headline"
                defaultMessage="Bewertungsübersicht"
              />
            </CardHeadline>
          }
        >
          <StarsWrapper>
            <RatingStars
              maskWidth={`${parseFloat(response.product.avgRating.toFixed(1)) * 20}%`}
              iconType={iconType}
            />
            <span>({response.product.reviewCount})</span>
          </StarsWrapper>
          <RecommendCountWrapper>
            <PercentageCircle summaryValues={response.summaryValues} />
            <div>
              <FormattedMessage
                id="ratings.recommend.count"
                defaultMessage="<label>{recocount} von {totalcount} Kunden</label> würden dieses Produkt einem Freund empfehlen."
                values={{
                  label: (txt) => <strong>{txt}</strong>,
                  recocount: response.summaryValues?.recommended,
                  totalcount: response.summaryValues?.reviews,
                }}
              />
            </div>
          </RecommendCountWrapper>
          {response.product.tags &&
            response.product.tags.pro &&
            response.product.tags.proAvailable && (
              <TagsWrapper>
                <CardHeadline className="headline">
                  <FormattedMessage id="ratings.summary.headline.pro" defaultMessage="Vorteile" />
                </CardHeadline>
                <Tags tags={response.product.tags.pro} />
              </TagsWrapper>
            )}
          {response.product.tags &&
            response.product.tags.contra &&
            response.product.tags.contraAvailable && (
              <TagsWrapper>
                <CardHeadline className="headline">
                  <FormattedMessage
                    id="ratings.summary.headline.contra"
                    defaultMessage="Nachteile"
                  />
                </CardHeadline>
                <Tags tags={response.product.tags.contra} />
              </TagsWrapper>
            )}
        </Card>
      )}
      <CreateRatingContextProvider>
        <CreateRating />
      </CreateRatingContextProvider>
    </RatingsSummaryWrapper>
  );
};

export default RatingsSummary;
